/**
 * Ticket 75 — the pure half of the top-gifters roller above the room chat.
 *
 * The rules that decide what the ticker says, how fast it moves, and whether it
 * appears at all live here rather than in the component, so they can be tested
 * without a DOM and without a marquee actually running.
 */

export interface TopGifterAccount {
  id: string;
  username: string;
  acct: string;
  display_name: string;
  avatar: string;
}

export interface TopGifterEntry {
  rank: number;
  subs: number;
  account: TopGifterAccount;
}

export interface TopGiftersBoard {
  period: string;
  resets_in_seconds: number;
  period_start?: string;
  period_end?: string;
  gift_subs_enabled?: boolean;
  host_account_id: string;
  total_subs: number;
  total_gifters: number;
  truncated_count: number;
  entries: TopGifterEntry[];
}

/**
 * How many names the moving ticker carries.
 *
 * Well under the number the board itself will show: the ticker is a glance, and
 * a loop long enough that a name comes round once a minute is not a ticker, it
 * is a wall. The rest are one click away in the expanded board.
 */
export const TICKER_LIMIT = 20;

/** Rows in the expanded board before it says "and N more". */
export const LIST_LIMIT = 50;

/**
 * Seconds per name in the marquee.
 *
 * The duration is derived from how many names are on the track rather than
 * fixed, so the SPEED is constant: a fixed duration would make a board of three
 * names crawl and a board of forty sprint, and the ticket asks for a pace that
 * does not strain the eyes at any size.
 */
export const SECONDS_PER_ENTRY = 4;

/** Never faster than this, however short the track. */
export const MIN_MARQUEE_SECONDS = 12;

/**
 * Is this payload something we can draw?
 *
 * Validated rather than trusted because the ticker renders inside the chat
 * panel: a malformed entry that throws while rendering would take the chat down
 * with it, which is a far worse outcome than an absent ticker.
 */
export const isUsableBoard = (
  board: TopGiftersBoard | null,
): board is TopGiftersBoard => {
  if (!board || !Array.isArray(board.entries)) return false;

  // Walked as `unknown` deliberately. The declared type says every field is
  // present, but this payload came off the network and the type is a claim
  // about it rather than a guarantee — checking it against its own optimistic
  // type would compile to no check at all.
  return (board.entries as unknown[]).every((row) => {
    const entry = row as Partial<TopGifterEntry> | null;

    return (
      !!entry &&
      !!entry.account &&
      typeof entry.account.id === 'string' &&
      Number.isFinite(entry.subs) &&
      (entry.subs ?? 0) > 0 &&
      Number.isFinite(entry.rank)
    );
  });
};

/**
 * Whether the roller belongs on screen at all.
 *
 * Gifting is off by default on this instance, so with nobody having ever gifted
 * there is nothing to say and nothing anyone could do about it — an empty
 * ticker parked permanently above the chat is dead furniture. Once gifting is
 * open, an empty board is worth showing, because "be the first" is an invitation
 * rather than an apology.
 */
export const shouldRenderTicker = (
  board: TopGiftersBoard | null,
  giftingEnabled: boolean,
): boolean => {
  if (!isUsableBoard(board)) return false;

  return board.entries.length > 0 || giftingEnabled;
};

/** The names the ticker carries, capped. */
export const tickerEntries = (board: TopGiftersBoard): TopGifterEntry[] =>
  board.entries.slice(0, TICKER_LIMIT);

/** The rows the expanded board shows, capped. */
export const listEntries = (board: TopGiftersBoard): TopGifterEntry[] =>
  board.entries.slice(0, LIST_LIMIT);

/**
 * How many gifters are not on screen.
 *
 * Counts both the ones the SERVER truncated and the ones trimmed by the client
 * cap, so the "and N more" is honest about everybody missing rather than only
 * about the half of them this layer happens to know about.
 */
export const hiddenGifterCount = (board: TopGiftersBoard): number => {
  const shown = Math.min(board.entries.length, LIST_LIMIT);
  const clientTrimmed = Math.max(board.entries.length - shown, 0);

  return Math.max(board.truncated_count, 0) + clientTrimmed;
};

/**
 * A whole-seconds countdown to the reset, in the coarsest unit that is still
 * true: "Resets in 3 days", "Resets in 5 hours", "Resets in 20 minutes".
 *
 * Coarse on purpose — a live-ticking second counter above a chat is a moving
 * distraction next to an already-moving ticker, and nobody needs to know that
 * the leaderboard clears in eleven days, four hours and nine seconds.
 */
export const formatResetCountdown = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'Resetting now';

  const days = Math.floor(seconds / 86_400);
  if (days >= 1) return `Resets in ${days} ${days === 1 ? 'day' : 'days'}`;

  const hours = Math.floor(seconds / 3_600);
  if (hours >= 1) return `Resets in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;

  const minutes = Math.floor(seconds / 60);
  if (minutes >= 1)
    return `Resets in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;

  return 'Resets in under a minute';
};

/** "12 subs" / "1 sub". */
export const formatSubs = (subs: number): string =>
  `${subs} ${subs === 1 ? 'sub' : 'subs'}`;

/** 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 11 -> "11th". */
export const ordinal = (n: number): string => {
  const abs = Math.abs(Math.trunc(n));
  const lastTwo = abs % 100;

  if (lastTwo >= 11 && lastTwo <= 13) return `${abs}th`;

  switch (abs % 10) {
    case 1:
      return `${abs}st`;
    case 2:
      return `${abs}nd`;
    case 3:
      return `${abs}rd`;
    default:
      return `${abs}th`;
  }
};

/**
 * How long one full loop of the marquee should take, for a given number of
 * names. See SECONDS_PER_ENTRY — this keeps the pixels-per-second constant.
 */
export const marqueeDuration = (entryCount: number): number =>
  Math.max(MIN_MARQUEE_SECONDS, entryCount * SECONDS_PER_ENTRY);

export interface ClimbHint {
  /** Subs needed to reach `rank`. Always at least 1. */
  subs: number;
  /** The position those subs would buy. */
  rank: number;
}

/**
 * "Gift 1 sub to take 7th" — what it would cost the viewer to move up one place.
 *
 * This is the line under the board in the operator's screenshot, and it is the
 * only part of this feature that is about the person reading it rather than
 * about the people on it.
 *
 * Rules, in order:
 *   - Nobody who is already 1st is told how to climb; there is nowhere to go.
 *   - Someone on the board is quoted the gap to the person directly above,
 *     plus the one sub that actually passes them. A gap of exactly the
 *     difference would only draw level, and level is not past.
 *   - Someone not on the board is quoted what it takes to pass the last person
 *     on it — or a single sub if the board has room, since the first sub gifted
 *     in an empty month takes 1st.
 *   - A viewer who is the host, or who is not signed in, gets nothing: neither
 *     can gift, so the hint would be an invitation to a door that is shut.
 */
export const climbHint = (
  board: TopGiftersBoard,
  viewerAccountId: string | null | undefined,
): ClimbHint | null => {
  if (!viewerAccountId) return null;
  if (viewerAccountId === board.host_account_id) return null;

  const entries = board.entries;
  const index = entries.findIndex(
    (entry) => entry.account.id === viewerAccountId,
  );

  if (index === 0) return null;

  if (index > 0) {
    const above = entries[index - 1];
    const mine = entries[index];
    if (!above || !mine) return null;

    return { subs: above.subs - mine.subs + 1, rank: above.rank };
  }

  const last = entries[entries.length - 1];

  // An empty board, or one that has not yet filled: a single sub takes the next
  // free position outright.
  if (!last) return { subs: 1, rank: 1 };
  if (entries.length < LIST_LIMIT && last.subs <= 1)
    return { subs: 1, rank: last.rank + 1 };

  return { subs: last.subs, rank: last.rank };
};

/**
 * The colour band for a gifter's icon, by how much they have given — the
 * gold/teal/green tiers in the operator's screenshot.
 */
export const giftTier = (subs: number): 'gold' | 'teal' | 'green' => {
  if (subs >= 25) return 'gold';
  if (subs >= 5) return 'teal';
  return 'green';
};
